import React, { useEffect, useRef, useState } from "react";
import { Badge, Box, Button, Chip, CircularProgress, IconButton, Popover, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { CampaignOutlined, Done, DoneAll, NotificationsNoneOutlined, NotificationsOffOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import useNotifications from "../../../hooks/useNotifications";
import { PUSH_DESTINATION_PATHS, isSafeInternalPath } from "../../../utils/pushDestinations";
import { notificationActionLabel } from "../../../utils/notificationUi";
import { getUserFromLocalStorage } from "../../../utils/utils";
import type { AppNotification } from "../../../types/notifications";

/** In-app target of a notification: server path (campaigns) or an allow-listed screen key. Never anything else. */
export function notificationPath(n: AppNotification): string | null {
  if (n.path && isSafeInternalPath(n.path)) return n.path;
  const key = (n.screen || "").trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(PUSH_DESTINATION_PATHS, key) ? PUSH_DESTINATION_PATHS[key] : null;
}

const isLoggedIn = (): boolean => {
  try {
    return !!getUserFromLocalStorage()?.userToken;
  } catch {
    return false;
  }
};

/** Same palette as the rest of the app (header menus #141414, active accent #f2674a, links #4a7af2). */
const C = {
  surface: "#141414",
  surfaceHover: "#ffffff0f",
  unreadRow: "#f2674a12",
  border: "#ffffff1a",
  text: "#ffffff",
  muted: "#9ca3af",
  body: "#d1d5db",
  accent: "#f2674a",
  blue: "#4a7af2",
} as const;

/* One-shot attention animation, played only when a NEW notification arrives (never on page load, never looping). */
const ring = keyframes`
  0% { transform: rotate(0); }
  10% { transform: rotate(16deg); }
  20% { transform: rotate(-14deg); }
  30% { transform: rotate(12deg); }
  40% { transform: rotate(-10deg); }
  50% { transform: rotate(6deg); }
  60% { transform: rotate(-4deg); }
  70%, 100% { transform: rotate(0); }
`;
const halo = keyframes`
  0% { box-shadow: 0 0 0 0 ${C.accent}99; }
  100% { box-shadow: 0 0 0 14px ${C.accent}00; }
`;
const pop = keyframes`
  0% { transform: scale(1) translate(50%, -50%); }
  40% { transform: scale(1.55) translate(50%, -50%); }
  100% { transform: scale(1) translate(50%, -50%); }
`;
const ANIMATION_MS = 2400;

/**
 * Header bell: the ONE place for everything the user may need to see - account activity (deposits, KYC, reminders...)
 * and announcements from the Wolfpack team - with a single unread badge, All / Unread tabs and mark-as-read
 * (per item, on click, or all at once). State lives in the shared notification store.
 */
const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { unread, items, loading, loaded, countReady, hasMore, filter, load, loadMore, setFilter, markRead, markAllRead } = useNotifications(isLoggedIn());
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [ringing, setRinging] = useState(0); // animation run id; 0 = idle
  const [announce, setAnnounce] = useState("");
  const prevUnread = useRef<number | null>(null);
  const anchorOpen = Boolean(anchor);

  // Animate when the count goes UP after the first value is known (a new notification), not on the first load.
  useEffect(() => {
    if (!countReady) return undefined;
    const prev = prevUnread.current;
    prevUnread.current = unread;
    if (prev === null || unread <= prev) return undefined;
    setAnnounce(`${unread - prev} new notification${unread - prev > 1 ? "s" : ""}`);
    if (anchorOpen) return undefined; // the list is already in front of the user
    setRinging((r) => r + 1);
    const t = window.setTimeout(() => setRinging(0), ANIMATION_MS);
    return () => window.clearTimeout(t);
  }, [unread, countReady, anchorOpen]);

  const open = (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
    setRinging(0);
    load();
  };

  const go = (n: AppNotification) => {
    if (n.read === false) markRead([n.id]);
    const path = notificationPath(n);
    if (path) {
      setAnchor(null);
      navigate(path);
    }
  };

  const animating = ringing > 0;
  const motionOk = "@media (prefers-reduced-motion: no-preference)";

  return (
    <>
      <IconButton
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        aria-haspopup="dialog"
        aria-expanded={anchorOpen}
        onClick={open}
        data-testid="notification-bell"
        sx={{ borderRadius: "50%", [motionOk]: animating ? { animation: `${halo} 1.1s ease-out 2` } : {} }}
      >
        <Badge
          badgeContent={unread}
          max={99}
          invisible={unread === 0}
          sx={{
            "& .MuiBadge-badge": {
              fontWeight: 700, backgroundColor: C.accent, color: "#fff", fontSize: "0.62rem", minWidth: "17px", height: "17px",
              [motionOk]: animating ? { animation: `${pop} 0.6s ease-out 2` } : {},
            },
          }}
        >
          {/* key restarts the animation for every new arrival */}
          <Box key={ringing} component="span" sx={{ display: "inline-flex", transformOrigin: "50% 8%", [motionOk]: animating ? { animation: `${ring} 1.2s ease-in-out 1` } : {} }}>
            <NotificationsNoneOutlined className="text-white" />
          </Box>
        </Badge>
      </IconButton>
      {/* Screen readers hear the arrival; visually hidden. */}
      <Box component="span" role="status" aria-live="polite" sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
        {announce}
      </Box>
      <Popover
        open={anchorOpen}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1, width: { xs: "calc(100vw - 32px)", sm: 400 }, maxHeight: 540, display: "flex", flexDirection: "column",
              background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: "12px", boxShadow: "0 12px 32px rgba(0,0,0,0.5)", overflow: "hidden",
            },
          },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" px={2} pt={1.5} pb={0.5}>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Notifications</Typography>
          <Button size="small" disabled={unread === 0} onClick={markAllRead} startIcon={<DoneAll fontSize="small" />} sx={{ textTransform: "none", color: C.blue, "&.Mui-disabled": { color: "#ffffff40" } }}>
            Mark all as read
          </Button>
        </Box>
        <Tabs
          value={filter}
          onChange={(_, v) => setFilter(v)}
          variant="fullWidth"
          sx={{
            minHeight: 38, borderBottom: `1px solid ${C.border}`,
            "& .MuiTab-root": { minHeight: 38, textTransform: "none", color: C.muted, fontWeight: 600 },
            "& .Mui-selected": { color: `${C.text} !important` },
            "& .MuiTabs-indicator": { backgroundColor: C.accent },
          }}
        >
          <Tab value="all" label="All" data-testid="notification-tab-all" />
          <Tab value="unread" label={unread ? `Unread (${unread > 99 ? "99+" : unread})` : "Unread"} data-testid="notification-tab-unread" />
        </Tabs>
        <Box sx={{ overflowY: "auto", flex: 1, scrollbarWidth: "thin", scrollbarColor: "#ffffff30 transparent" }}>
          {!loaded && loading && (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={22} sx={{ color: C.accent }} /></Box>
          )}
          {loaded && items.length === 0 && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={5} px={3}>
              <NotificationsOffOutlined sx={{ fontSize: 36, color: "#ffffff40" }} />
              <Typography sx={{ textAlign: "center", color: C.muted, fontSize: 14 }}>
                {filter === "unread" ? "No unread notifications." : "You're all caught up."}
              </Typography>
            </Box>
          )}
          {items.map((n) => {
            const isUnread = n.read === false;
            const announcement = n.source === "CAMPAIGN";
            const path = notificationPath(n);
            return (
              <Box
                key={n.id}
                data-testid="notification-item"
                role="button"
                tabIndex={0}
                onClick={() => go(n)}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return; // let inner buttons handle their own keys
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    go(n);
                  }
                }}
                display="flex"
                gap={1.5}
                px={2}
                py={1.5}
                sx={{
                  cursor: "pointer", borderBottom: `1px solid ${C.border}`, background: isUnread ? C.unreadRow : "transparent",
                  borderLeft: `3px solid ${isUnread ? C.accent : "transparent"}`, transition: "background .15s",
                  "&:hover, &:focus-visible": { background: C.surfaceHover, outline: "none" },
                }}
              >
                <Box minWidth={0} flex={1}>
                  <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                    <Typography sx={{ fontWeight: isUnread ? 700 : 500, fontSize: 14, color: isUnread ? C.text : C.body }}>{n.title}</Typography>
                    {announcement && (
                      <Chip size="small" icon={<CampaignOutlined sx={{ fontSize: 14, color: `${C.blue} !important` }} />} label="Announcement" sx={{ height: 18, fontSize: 10, color: "#bcd0ff", background: `${C.blue}26` }} />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13, color: C.body, wordBreak: "break-word", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{n.body}</Typography>
                  <Box display="flex" alignItems="center" gap={1.5} mt={0.75}>
                    <Typography sx={{ fontSize: 11, color: C.muted }}>{moment(n.createdAt).fromNow()}</Typography>
                    {path && (
                      <Button
                        size="small"
                        data-testid="notification-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          go(n);
                        }}
                        sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, height: 24, px: 1.5, borderRadius: "50px", color: "#fff", background: C.blue, "&:hover": { background: "#3c68d8" } }}
                      >
                        {notificationActionLabel(n.screen)}
                      </Button>
                    )}
                  </Box>
                </Box>
                {isUnread && (
                  <Tooltip title="Mark as read">
                    <IconButton
                      size="small"
                      aria-label="Mark as read"
                      data-testid="notification-mark-read"
                      onClick={(e) => {
                        e.stopPropagation();
                        markRead([n.id]);
                      }}
                      sx={{ alignSelf: "flex-start", color: C.muted, "&:hover": { color: "#4edf6d" } }}
                    >
                      <Done fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          })}
          {hasMore && (
            <Box display="flex" justifyContent="center" py={1}>
              <Button size="small" disabled={loading} onClick={loadMore} sx={{ textTransform: "none", color: C.blue }}>
                {loading ? "Loading…" : "Load more"}
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
